import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react';

/**
 * FormEditingContext - Tracks when a user is actively editing a form
 *
 * Purpose:
 * - Prevents automatic query invalidation from disrupting form editing
 * - Used by AuthContext to skip `queryClient.invalidateQueries()` when user is on form screens
 * - Helps prevent data loss when session validation triggers during form entry
 *
 * Architecture:
 * - Uses both React Context (for components) and module-level state (for non-React code like AuthContext)
 * - The module-level state is synced with the React context state
 * - This allows AuthContext to check the state without creating circular dependencies
 *
 * Usage:
 * - Wrap screens/components that have forms with FormEditingProvider
 * - Call setIsEditing(true) when user starts filling a form
 * - Call setIsEditing(false) when form is submitted or user navigates away
 * - AuthContext uses formEditingState.isEditing() to check before invalidating queries
 */

// ============================================================================
// Module-level state for non-React code access (e.g., AuthContext)
// ============================================================================

interface FormEditingState {
  /** Check if a form is currently being edited */
  isEditing: () => boolean;
  /** Get the current editing screen name */
  getEditingScreen: () => string | null;
  /** Set the editing state (used internally by React context) */
  _setEditing: (editing: boolean, screen?: string | null) => void;
}

let _isFormEditing = false;
let _editingScreen: string | null = null;

/**
 * Module-level form editing state
 * Can be imported directly by AuthContext without React context dependencies
 */
export const formEditingState: FormEditingState = {
  isEditing: () => _isFormEditing,
  getEditingScreen: () => _editingScreen,
  _setEditing: (editing: boolean, screen?: string | null) => {
    _isFormEditing = editing;
    _editingScreen = screen ?? null;
    // State update - synced with React context
  },
};

// ============================================================================
// React Context for component-level access
// ============================================================================

interface FormEditingContextType {
  /** Whether the user is currently editing a form (CreateRequest or RequestDetail in edit mode) */
  isFormEditing: boolean;
  /** Set the form editing state */
  setIsEditing: (editing: boolean) => void;
  /** The current screen being edited (for debugging) */
  editingScreen: string | null;
  /** Set the current screen being edited */
  setEditingScreen: (screen: string | null) => void;
}

const FormEditingContext = createContext<FormEditingContextType | undefined>(undefined);

interface FormEditingProviderProps {
  children: ReactNode;
}

export const FormEditingProvider: React.FC<FormEditingProviderProps> = ({ children }) => {
  const [isFormEditing, setIsFormEditing] = useState(false);
  const [editingScreen, setEditingScreenState] = useState<string | null>(null);

  // Sync React state with module-level state
  useEffect(() => {
    formEditingState._setEditing(isFormEditing, editingScreen);
  }, [isFormEditing, editingScreen]);

  const setIsEditing = useCallback((editing: boolean) => {
    setIsFormEditing(editing);
    if (!editing) {
      setEditingScreenState(null);
    }
  }, []);

  const setEditingScreen = useCallback((screen: string | null) => {
    setEditingScreenState(screen);
    if (screen) {
      setIsFormEditing(true);
    }
  }, []);

  const contextValue: FormEditingContextType = useMemo(() => ({
    isFormEditing,
    setIsEditing,
    editingScreen,
    setEditingScreen,
  }), [isFormEditing, setIsEditing, editingScreen, setEditingScreen]);

  return (
    <FormEditingContext.Provider value={contextValue}>
      {children}
    </FormEditingContext.Provider>
  );
};

/**
 * Hook to access the form editing context
 * Returns a safe default when used outside the provider (for backwards compatibility)
 */
export const useFormEditing = (): FormEditingContextType => {
  const context = useContext(FormEditingContext);

  // Return safe defaults if context is not available
  // This allows components to use the hook even outside the provider
  if (context === undefined) {
    return {
      isFormEditing: false,
      setIsEditing: () => {},
      editingScreen: null,
      setEditingScreen: () => {},
    };
  }

  return context;
};

export default FormEditingContext;
